import { useCallback, useMemo, useState } from "react";
import { Pressable, StyleSheet, useWindowDimensions, View } from "react-native";
import { NavigationProp, useNavigation } from "@react-navigation/native";

import { useGetAssignmentsWithCourseQuery } from "../../../graphql/generated/graphql";
import { colors, radii, semanticColors, spacing, typographyScale } from "../../../styles/design-system";
import { ArrowRight, FileText } from "lucide-react";
import { useAppConfig } from "../../../AppConfigProvider";
import { useWorkspaceThreads } from "../../../contexts/CompanionThreadsContext";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import CustomText from "../../common/general/CustomText/CustomText";
import type { Assignment } from "../../chatNewInterface/interfaceComponents/modals/AddAssignmentModal";

export const UpcomingAssignments = ({
  onSelectAssignment,
}: {
  onSelectAssignment?: (assignment: Assignment) => void;
}) => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const appConfig = useAppConfig();
  const schoolId = appConfig?.axioSchools[0];
  const { width } = useWindowDimensions();
  const isMedium = width >= 768;
  const { setActiveWorkspaceId, setActiveThreadId } = useWorkspaceThreads();

  const { data } = useGetAssignmentsWithCourseQuery({
    variables: {
      schoolId,
    },
    skip: !schoolId,
    fetchPolicy: "network-only",
  });
  const upcomingAssignments = data?.getAssignmentsWithCourse?.data ?? [];

  const handleAssignmentClick = useCallback(
    (assignment: Assignment) => {
      onSelectAssignment?.(assignment);
    },
    [onSelectAssignment]
  );

  const [hover, setHover] = useState<string | undefined>();
  const [hoveredBadgeId, setHoveredBadgeId] = useState<string | undefined>();

  const handleWorkspaceBadgeClick = useCallback(
    (event: { stopPropagation?: () => void } | undefined, workspaceId?: string | null) => {
      event?.stopPropagation?.();
      if (!workspaceId) return;
      setActiveWorkspaceId(workspaceId);
      setActiveThreadId(null);
      navigation.navigate("Companion Chat", { chatId: undefined });
    },
    [navigation, setActiveWorkspaceId, setActiveThreadId]
  );

  const badgePalette = {
    backgroundColor: colors.brand[100],
    hoverBackgroundColor: `${colors.brand[200]}cc`,
    textColor: colors.brand[900],
  };

  const displayAssignments = useMemo(
    () =>
      [...upcomingAssignments]
        .filter(
          (assignment) => assignment.due_date == null || new Date(assignment.due_date ?? "").getTime() >= Date.now()
        )
        .sort((a, b) => new Date(a.due_date ?? "").getTime() - new Date(b.due_date ?? "").getTime())
        .slice(0, 3),
    [upcomingAssignments]
  );

  const headerIconSize = isMedium ? 20 : 18;
  const itemIconSize = 16;

  return (
    <View style={[styles.container, isMedium && styles.containerMd]}>
      <View style={[styles.header, isMedium && styles.headerMd]}>
        <FileText size={headerIconSize} color={colors.grey[600]} />
        <CustomText style={styles.headerText} text="Upcoming Assignments" numberOfLines={1} ellipsizeMode="tail" />
      </View>
      {displayAssignments.length > 0 ? (
        <View style={[styles.list, isMedium && styles.listMd]}>
          {displayAssignments.map((assignment) => (
            <Pressable
              key={assignment.id}
              style={[styles.listItem, isMedium && styles.listItemMd, hover === assignment.id && styles.activeItem]}
              onPress={() => handleAssignmentClick(assignment as Assignment)}
              onMouseEnter={() => setHover(assignment.id)}
              onMouseLeave={() => setHover(undefined)}>
              {isMedium && <FileText size={itemIconSize} color={colors.brand[500]} />}
              <View style={styles.itemContent}>
                <CustomText
                  style={[styles.itemName, hover === assignment.id && styles.activeItemName]}
                  text={assignment.title}
                  useTranslationText={false}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                />
                <View style={[styles.itemMetaRow, isMedium && styles.itemMetaRowMd]}>
                  {assignment.courseName ? (
                    <Pressable
                      pointerEvents="none"
                      disabled={true}
                      onPress={(event) => handleWorkspaceBadgeClick(event, assignment.courseId)}
                      onMouseEnter={() => setHoveredBadgeId(assignment.id)}
                      onMouseLeave={() => setHoveredBadgeId(undefined)}
                      accessibilityRole="button"
                      accessibilityLabel={`Open ${assignment.courseName} workspace`}
                      style={[
                        styles.badge,
                        { backgroundColor: badgePalette.backgroundColor },
                        hoveredBadgeId === assignment.id && { backgroundColor: badgePalette.hoverBackgroundColor },
                      ]}>
                      <CustomText
                        style={[styles.badgeText, { color: badgePalette.textColor }]}
                        text={assignment.courseName}
                        useTranslationText={false}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      />
                    </Pressable>
                  ) : null}
                  <CustomText
                    style={styles.itemTime}
                    text={
                      assignment.due_date != null
                        ? `Due ${new Date(assignment.due_date).toLocaleString(undefined, {
                            weekday: "long",
                            month: "short",
                            day: "numeric",
                          })}`
                        : "No due date"
                    }
                    useTranslationText={false}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  />
                </View>
              </View>
              {isMedium && (
                <ArrowRight size={16} color={colors.grey[400]} style={{ opacity: hover === assignment.id ? 1 : 0 }} />
              )}
            </Pressable>
          ))}
        </View>
      ) : (
        <View style={styles.emptyState}>
          <FileText size={28} color={colors.grey[300]} />
          <CustomText style={styles.emptyTitle} text="No upcoming assignments" useTranslationText={false} />
          <CustomText
            style={styles.emptySubtitle}
            text="Assignments from your classes will appear here"
            useTranslationText={false}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignSelf: "stretch",
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: "rgba(208, 213, 221, 0.5)",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: spacing.sm,
    shadowColor: colors.grey[900],
    shadowOffset: {
      width: 0,
      height: 24,
    },
    shadowOpacity: 0.18,
    shadowRadius: 48,
    overflow: "hidden",
    // @ts-expect-error - web-only blur
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },
  containerMd: {
    padding: spacing.md,
    maxWidth: 320,
    minWidth: 260,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    marginBottom: 12,
  },
  headerMd: {
    marginBottom: spacing.sm,
  },
  headerText: {
    color: colors.grey[900],
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: 600,
  },
  list: {
    padding: spacing.xs,
    display: "flex",
    gap: spacing.xs,
    height: 300,
  },
  listMd: {
    height: "auto",
  },
  listItem: {
    display: "flex",
    flexDirection: "row",
    gap: spacing.xs,
    borderRadius: radii.md,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    marginHorizontal: -spacing.xs,
    cursor: "pointer",
  },
  listItemMd: {
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    marginHorizontal: -12,
  },
  activeItem: {
    backgroundColor: colors.grey[50],
  },
  itemContent: {
    flex: 1,
  },
  itemName: {
    color: colors.grey[900],
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: 500,
    lineHeight: 21,
  },
  itemMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    marginTop: spacing.xxs,
    minWidth: 0,
  },
  itemMetaRowMd: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: spacing.xxs,
  },
  badge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: radii.full,
    cursor: "pointer",
    flexShrink: 1,
    maxWidth: "100%",
  },
  badgeText: {
    fontSize: 11,
    fontWeight: 600,
    flexShrink: 1,
  },
  activeItemName: {
    color: semanticColors.typography.textBrand,
  },
  itemTime: {
    color: semanticColors.typography.textLight,
    fontSize: typographyScale.textXs.fontSize,
    flexShrink: 0,
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: spacing.lg,
    gap: 6,
  },
  emptyTitle: {
    color: colors.grey[500],
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: 500,
  },
  emptySubtitle: {
    color: colors.grey[400],
    fontSize: typographyScale.textXs.fontSize,
  },
});
